import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { publicNewsApi } from "../api/newsApi";
import type {
  NewsListItem,
  PagedResult,
} from "../../types";
import {
  absoluteAssetUrl,
  formatDate,
  getErrorMessage,
} from "../utils/utils";
import "../styles/News.css";
import "../styles/NewsRating.css";
import "../styles/NewsArticleStyle.css";

const empty: PagedResult<NewsListItem> = {
  items: [],
  totalItems: 0,
  page: 1,
  pageSize: 9,
  totalPages: 0,
};

const getNewsRating = (
  item: NewsListItem
) => {
  const raw = item as NewsListItem & {
    rating?: number;
    customerRatingAverage?: number;
    customerRatingCount?: number;
    overallRating?: number;
  };

  const adminRating = Math.min(
    5,
    Math.max(
      0,
      Number(raw.rating) || 0
    )
  );

  const customerAverage = Math.min(
    5,
    Math.max(
      0,
      Number(
        raw.customerRatingAverage
      ) || 0
    )
  );

  const customerCount = Math.max(
    0,
    Number(
      raw.customerRatingCount
    ) || 0
  );

  const overall = Math.min(
    5,
    Math.max(
      0,
      Number(raw.overallRating) ||
        customerAverage ||
        adminRating
    )
  );

  return {
    adminRating,
    customerAverage,
    customerCount,
    overall,
  };
};

const NewsRatingDisplay: React.FC<{
  item: NewsListItem;
  compact?: boolean;
}> = ({
  item,
  compact = false,
}) => {
  const rating = getNewsRating(item);

  if (rating.overall <= 0) {
    return null;
  }

  return (
    <div
      className={`news-rating-display ${
        compact ? "is-compact" : ""
      }`}
      aria-label={`${rating.overall.toFixed(
        1
      )} trên 5 sao`}
      title={
        rating.customerCount > 0
          ? `${rating.customerAverage.toFixed(
              1
            )}/5 từ ${
              rating.customerCount
            } lượt khách hàng`
          : `King Jades: ${rating.adminRating}/5`
      }
    >
      <span className="news-rating-display__stars">
        {[1, 2, 3, 4, 5].map(
          (star) => (
            <i
              key={star}
              className={
                star <=
                Math.round(
                  rating.overall
                )
                  ? "is-active"
                  : ""
              }
            >
              ★
            </i>
          )
        )}
      </span>

      <b>
        {rating.overall.toFixed(1)}/5
      </b>

      {rating.customerCount > 0 ? (
        <small>
          ({rating.customerCount})
        </small>
      ) : null}
    </div>
  );
};

const NewsPage: React.FC = () => {
  const [data, setData] =
    React.useState(empty);
  const [featured, setFeatured] =
    React.useState<NewsListItem[]>([]);
  const [search, setSearch] =
    React.useState("");
  const [submitted, setSubmitted] =
    React.useState("");
  const [page, setPage] =
    React.useState(1);
  const [loading, setLoading] =
    React.useState(true);
  const [error, setError] =
    React.useState("");

  React.useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError("");

      try {
        const [
          pageData,
          featuredData,
        ] = await Promise.all([
          publicNewsApi.getPage({
            search: submitted,
            page,
            pageSize: 9,
          }),
          publicNewsApi.getFeatured(3),
        ]);

        if (mounted) {
          setData(pageData);
          setFeatured(featuredData);
        }
      } catch (e) {
        if (mounted) {
          setError(
            getErrorMessage(
              e,
              "Không tải được tin tức."
            )
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [page, submitted]);

  return (
    <div className="public-news-page">
      <Header />

      <main>
        <section className="news-public-hero">
          <div>
            <span>KING JADES JOURNAL</span>

            <h1>
              Chuyện ngọc, kiến thức và phong cách sống
            </h1>

            <p>
              Khám phá câu chuyện về ngọc, cách chăm sóc
              sản phẩm và giá trị tinh tế trong từng lựa chọn.
            </p>
          </div>
        </section>

        {featured.length ? (
          <section className="news-public-section featured">
            <div className="news-public-heading">
              <div>
                <span>FEATURED STORIES</span>
                <h2>Bài viết nổi bật</h2>
              </div>
            </div>

            <div className="featured-grid">
              {featured.map(
                (item, index) => (
                  <article
                    className={
                      index === 0
                        ? "large"
                        : ""
                    }
                    key={item.id}
                  >
                    <Link
                      className="news-image"
                      to={`/news/${item.slug}`}
                    >
                      {item.coverImageUrl ? (
                        <img
                          src={absoluteAssetUrl(
                            item.coverImageUrl
                          )}
                          alt={item.title}
                        />
                      ) : (
                        <span>KJ</span>
                      )}
                    </Link>

                    <div>
                      <small>
                        {item.category ||
                          "Tin tức"}
                      </small>

                      <h3>
                        <Link
                          to={`/news/${item.slug}`}
                        >
                          {item.title}
                        </Link>
                      </h3>

                      <NewsRatingDisplay
                        item={item}
                        compact
                      />

                      <p>{item.summary}</p>

                      <em>
                        {formatDate(
                          item.publishedAtUtc
                        )}
                      </em>
                    </div>
                  </article>
                )
              )}
            </div>
          </section>
        ) : null}

        <section className="news-public-section">
          <div className="news-public-heading">
            <div>
              <span>LATEST NEWS</span>
              <h2>Tất cả bài viết</h2>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(
                  search.trim()
                );
                setPage(1);
              }}
            >
              <input
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
                placeholder="Tìm bài viết..."
              />

              <button>Tìm</button>
            </form>
          </div>

          {error ? (
            <div className="news-alert error">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="news-loading">
              Đang tải...
            </div>
          ) : !data.items.length ? (
            <div className="news-empty">
              Chưa có bài viết.
            </div>
          ) : (
            <div className="news-public-grid">
              {data.items.map(
                (item) => (
                  <article key={item.id}>
                    <Link
                      className="news-image"
                      to={`/news/${item.slug}`}
                    >
                      {item.coverImageUrl ? (
                        <img
                          src={absoluteAssetUrl(
                            item.coverImageUrl
                          )}
                          alt={item.title}
                        />
                      ) : (
                        <span>KJ</span>
                      )}
                    </Link>

                    <div>
                      <small>
                        {item.category ||
                          "Tin tức"}
                      </small>

                      <h3>
                        <Link
                          to={`/news/${item.slug}`}
                        >
                          {item.title}
                        </Link>
                      </h3>

                      <NewsRatingDisplay
                        item={item}
                        compact
                      />

                      <p>{item.summary}</p>

                      <footer>
                        <em>
                          {formatDate(
                            item.publishedAtUtc
                          )}
                        </em>

                        <Link
                          to={`/news/${item.slug}`}
                        >
                          Đọc bài →
                        </Link>
                      </footer>
                    </div>
                  </article>
                )
              )}
            </div>
          )}

          {data.totalPages > 1 ? (
            <div className="news-pagination">
              <button
                disabled={page <= 1}
                onClick={() =>
                  setPage(
                    (p) => p - 1
                  )
                }
              >
                ← Trước
              </button>

              <span>
                Trang {data.page}/
                {data.totalPages}
              </span>

              <button
                disabled={
                  page >=
                  data.totalPages
                }
                onClick={() =>
                  setPage(
                    (p) => p + 1
                  )
                }
              >
                Sau →
              </button>
            </div>
          ) : null}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NewsPage;
